import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useQuery } from '@tanstack/react-query';
import { ArrowDownToLine, ArrowUpFromLine } from 'lucide-react';
import { useState } from 'react';
import { Badge, DataTable, EmptyState, Field, PageHeader, Select } from '@/components/ui';
import { api } from '@/lib/api';
const KIND_LABELS = {
    receipt: 'Receipt', issue: 'Issue', transfer_out: 'Transfer out', transfer_in: 'Transfer in',
    adjustment: 'Adjustment', scrap: 'Scrap', prod_in: 'Production in', prod_out: 'Production out',
    return_in: 'Customer return', return_out: 'Vendor return', opening: 'Opening',
};
const KIND_TONE = {
    receipt: 'success', return_in: 'success', transfer_in: 'info', prod_in: 'success', opening: 'neutral',
    issue: 'warning', return_out: 'warning', transfer_out: 'info', prod_out: 'warning',
    adjustment: 'warning', scrap: 'danger',
};
export function StockMovesPage() {
    const warehouses = useQuery({
        queryKey: ['warehouses'],
        queryFn: async () => (await api.get('/inventory/warehouses/')).data,
    });
    const [warehouse, setWarehouse] = useState('');
    const [kind, setKind] = useState('');
    const moves = useQuery({
        queryKey: ['stock-moves', warehouse, kind],
        queryFn: async () => (await api.get('/inventory/moves/', {
            params: { ...(warehouse && { warehouse }), ...(kind && { kind }) },
        })).data,
    });
    return (_jsxs("div", { className: "space-y-6", children: [_jsx(PageHeader, { title: "Stock movements", description: "The inventory ledger \u2014 every quantity change. Authoritative source for on-hand." }), _jsxs("div", { className: "grid grid-cols-1 gap-3 md:grid-cols-3", children: [_jsx(Field, { label: "Warehouse", children: _jsxs(Select, { value: warehouse, onChange: (e) => setWarehouse(e.target.value), children: [_jsx("option", { value: "", children: "All warehouses" }), warehouses.data?.map((w) => (_jsxs("option", { value: w.id, children: [w.code, " \u00B7 ", w.name] }, w.id)))] }) }), _jsx(Field, { label: "Kind", children: _jsxs(Select, { value: kind, onChange: (e) => setKind(e.target.value), children: [_jsx("option", { value: "", children: "All kinds" }), Object.entries(KIND_LABELS).map(([k, label]) => (_jsx("option", { value: k, children: label }, k)))] }) })] }), _jsx(DataTable, { rows: moves.data, loading: moves.isLoading, rowKey: (r) => r.id, empty: _jsx(EmptyState, { icon: _jsx(ArrowDownToLine, { className: "h-6 w-6" }), title: "No movements", description: "Receipts, issues, transfers, and adjustments will appear here." }), columns: [
                    { key: 'date', header: 'Date', render: (m) => _jsx("span", { className: "text-muted-foreground", children: m.move_date }) },
                    {
                        key: 'kind', header: 'Kind',
                        render: (m) => (_jsx(Badge, { tone: KIND_TONE[m.kind] ?? 'neutral', dot: true, children: KIND_LABELS[m.kind] ?? m.kind })),
                    },
                    { key: 'sku', header: 'SKU', render: (m) => _jsx("code", { className: "font-mono text-xs", children: m.product_sku }) },
                    {
                        key: 'wh', header: 'Warehouse',
                        render: (m) => _jsx("span", { className: "text-muted-foreground", children: m.warehouse_code }),
                    },
                    {
                        key: 'qty', header: 'Qty', align: 'right',
                        render: (m) => (_jsxs("span", { className: `inline-flex items-center gap-1 font-mono ${Number(m.quantity) < 0 ? 'text-red-600' : 'text-emerald-600'}`, children: [Number(m.quantity) < 0 ? _jsx(ArrowUpFromLine, { className: "h-3 w-3" }) : _jsx(ArrowDownToLine, { className: "h-3 w-3" }), m.quantity] })),
                    },
                    {
                        key: 'unitCost', header: 'Unit cost', align: 'right',
                        render: (m) => _jsx("span", { className: "font-mono text-muted-foreground", children: m.unit_cost }),
                    },
                    {
                        key: 'total', header: 'Total', align: 'right',
                        render: (m) => _jsx("span", { className: "font-mono", children: m.total_cost }),
                    },
                    {
                        key: 'note', header: 'Note',
                        render: (m) => _jsx("span", { className: "text-xs text-muted-foreground", children: m.note }),
                    },
                ] })] }));
}
